import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { AccountMenu } from '../../components/AccountMenu';
import {
  IconBuilding,
  IconCard,
  IconClose,
  IconGauge,
  IconList,
  IconMail,
  IconMenu,
  IconSettings,
  IconUsers,
} from '../../components/Icons';

/**
 * `owner` marks a destination the server reserves for the organisation owner, and
 * `billing` one that needs the billing permission. Branch staff reach the portal too, so
 * showing them a link that can only answer 403 would be a worse welcome than not showing
 * it at all.
 */
const LINKS = [
  { to: '/owner', label: 'Dashboard', end: true, Icon: IconGauge },
  { to: '/owner/businesses', label: 'My businesses', Icon: IconBuilding, owner: true },
  { to: '/owner/branches', label: 'Branches & staff', Icon: IconUsers },
  { to: '/owner/listings', label: 'All listings', Icon: IconList },
  { to: '/owner/enquiries', label: 'Customer enquiries', Icon: IconMail },
  { to: '/owner/billing', label: 'Billing & payments', Icon: IconCard, billing: true },
  { to: '/owner/account', label: 'Account settings', Icon: IconSettings },
];

export function OwnerLayout() {
  const { user, isOwner, isBranchEmployee, canManageBilling } = useAuth();

  const links = LINKS.filter(
    (link) => (!link.owner || isOwner) && (!link.billing || canManageBilling),
  ).map((link) =>
    link.to === '/owner/branches' && isBranchEmployee && !isOwner
      ? { ...link, label: 'My branches' }
      : link,
  );
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);

  // Any navigation closes the drawer; leaving it open over the new page is disorienting
  // and hides the content the tap was meant to reach.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // While the drawer is over the page, stop the page behind it from scrolling, and let
  // Escape dismiss it.
  useEffect(() => {
    if (!navOpen) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen]);

  const branchCount = user?.businesses.length ?? 0;

  return (
    <div className="sf-shell">
      <a className="sf-skip-link" href="#portal-main">
        Skip to content
      </a>

      <header className="sf-topbar">
        <div className="sf-topbar__inner">
          <button
            type="button"
            className="sf-icon-btn sf-topbar__burger"
            aria-expanded={navOpen}
            aria-controls="owner-nav"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
          >
            <IconMenu />
          </button>

          <Link to="/owner" className="sf-topbar__brand sf-topbar__brand--mobile">
            RoomKart <span>Partner</span>
          </Link>

          <div className="sf-topbar__spacer" />

          <AccountMenu />
        </div>
      </header>

      <div className="sf-shell__body">
        {navOpen && (
          <div className="sf-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
        )}

        <nav
          id="owner-nav"
          className={`sf-sidenav${navOpen ? ' is-open' : ''}`}
          aria-label="Owner portal"
        >
          <div className="sf-sidenav__head">
            <Link to="/owner" className="sf-sidenav__brand">
              <span className="sf-sidenav__brandmark" aria-hidden="true">
                <IconBuilding />
              </span>
              <span>
                <strong>RoomKart</strong>
                <small>Partner</small>
              </span>
            </Link>
            <button
              ref={closeRef}
              type="button"
              className="sf-icon-btn sf-sidenav__close"
              aria-label="Close navigation"
              onClick={() => setNavOpen(false)}
            >
              <IconClose />
            </button>
          </div>

          <div className="sf-sidenav__links">
            {links.map(({ to, label, end, Icon }) => (
              <NavLink key={to} to={to} end={end} className="sf-sidenav__link">
                <Icon className="sf-sidenav__icon" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>

          {isOwner && canManageBilling && (
            <Link to="/owner/billing" className="sf-sidenav__upgrade">
              <span className="sf-sidenav__upgrade-icon" aria-hidden="true">✦</span>
              <strong>Grow your business</strong>
              <span>Add more branches and manage your subscriptions.</span>
              <span className="sf-sidenav__upgrade-link">View plans <span aria-hidden="true">→</span></span>
            </Link>
          )}

          {user && (
            <div className="sf-sidenav__foot">
              <div className="sf-sidenav__footName">{user.fullName}</div>
              <div className="sf-sidenav__footMeta">
                {branchCount === 0
                  ? 'No business yet'
                  : `${branchCount} business${branchCount === 1 ? '' : 'es'}`}
              </div>
            </div>
          )}
        </nav>

        <main id="portal-main" className="sf-shell__main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
